import type { IntegrationItemDto } from '@fos/shared/contracts/integrations';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '../../../ui/Button';
import type { Tone } from '../../../ui/Status';
import { StatusPill } from '../../../ui/StatusPill';
import { TOUCH_BUTTON } from '../../mobile';
import { INTEGRATION_ICONS, itemDescription, itemTitle, statusText, statusTone } from './integrations-ui';

/**
 * Wiersz integracji (Figma 12 „Ustawienia / Integracje”): kafelek 36 px z ikoną, nazwa (semibold) i podpis
 * (konto · synchronizacja albo opis rodzaju), status (kropka + tekst), jedna akcja: „Ustawienia” (Ghost) dla
 * zapisanej integracji, „Skonfiguruj” (Secondary) dla niepodłączonej — kalendarz: okno „Kalendarz (link iCal)”.
 * Mobile: status w podpisie.
 */

export function IntegrationTile({
  icon: Icon,
  title,
  description,
  status,
  action,
}: {
  icon: LucideIcon;
  title: ReactNode;
  description: ReactNode;
  status: { tone: Tone; label: string } | null;
  action: ReactNode;
}) {
  return (
    <div className="flex items-center gap-3.5 border-b border-line px-1 py-3.5">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-surface">
        <Icon size={18} strokeWidth={1.75} aria-hidden className="text-fg-secondary" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-body-s font-semibold text-fg">{title}</span>
        <span className="truncate text-caption text-fg-muted">
          {status && <span className="sm:hidden">{status.label} · </span>}
          {description}
        </span>
      </div>
      {status && (
        <StatusPill tone={status.tone} className="hidden sm:inline-flex">
          {status.label}
        </StatusPill>
      )}
      <div className="flex shrink-0 items-center">{action}</div>
    </div>
  );
}

export function IntegrationRow({
  item,
  onOpen,
  onConfigure,
}: {
  item: IntegrationItemDto;
  onOpen: (item: IntegrationItemDto) => void;
  onConfigure: (item: IntegrationItemDto) => void;
}) {
  const label = itemTitle(item);
  const virtual = item.id === null;
  let action: ReactNode;
  const description = itemDescription(item);

  if (virtual && item.managedBy === 'app') {
    action = item.canManage ? (
      <Button
        variant="secondary"
        size="s"
        onClick={() => onConfigure(item)}
        aria-label={`Skonfiguruj: ${label}`}
        className={TOUCH_BUTTON}
      >
        Skonfiguruj
      </Button>
    ) : null;
  } else {
    action = (
      <Button
        variant="ghost"
        size="s"
        onClick={() => onOpen(item)}
        aria-label={`Ustawienia: ${label}`}
        className={TOUCH_BUTTON}
      >
        Ustawienia
      </Button>
    );
  }

  return (
    <IntegrationTile
      icon={INTEGRATION_ICONS[item.kind]}
      title={label}
      description={description}
      status={{ tone: statusTone(item), label: statusText(item) }}
      action={action}
    />
  );
}
